import React, { ReactNode } from 'react';
import type { FallbackProps } from 'react-error-boundary';
import { Text, Flex, Link, Box } from '@chakra-ui/react';
import { ApolloError } from '@apollo/client';
import Heading from '#coreComponents/Heading';
import TheLogo from '#coreComponents/logos/TheLogo';
import { usePermissions } from '#core/Permissions';

type FallbackPropsWithLogout = FallbackProps & {
  id: string;
  footerComponent?: React.FC<{}>;
};

const errorSuffix =
  'Our team has been notified and is working to resolve the issue.';

export const getErrorTitle = (
  id: string,
  error: Error | undefined,
  isDeveloper: boolean,
): string => {
  if (error instanceof ApolloError) {
    const apolloError = error as ApolloError;
    if (apolloError.networkError) {
      return isDeveloper
        ? `${id}: Network Error`
        : 'There was an error connecting to the server';
    }
    if (apolloError.graphQLErrors.length > 0) {
      return isDeveloper
        ? `${id}: GraphQL Error ${JSON.stringify(apolloError.graphQLErrors)}`
        : 'There was an error querying for data';
    }
  }
  return isDeveloper
    ? `${id}: ${error?.name || 'No Error'}`
    : 'There was an error loading Datapoints';
};

export const getErrorMessage = (
  id: string,
  error: Error | undefined,
  isDeveloper: boolean,
): string | ReactNode => {
  if (error instanceof ApolloError) {
    const apolloError = error as ApolloError;
    if (apolloError.networkError) {
      return isDeveloper
        ? `Network Error: ${apolloError.networkError.message}`
        : `There was an error connecting to the Datapoints server, please try refreshing the app. ${errorSuffix}`;
    }
    if (apolloError.graphQLErrors.length > 0) {
      return isDeveloper ? (
        <>{JSON.stringify(apolloError.graphQLErrors)}</>
      ) : (
        `There was an error fetching Datapoints data, please try refreshing the app. ${errorSuffix}`
      );
    }
  }
  return isDeveloper
    ? `${error?.message || 'No Error Message'}`
    : `There was an error loading Datapoints, please try refreshing the app. ${errorSuffix}`;
};

const ErrorMessageFull: React.FC<FallbackPropsWithLogout> = ({
  id,
  error,
  footerComponent: FooterComponent,
}) => {
  const { hasRole } = usePermissions();
  const isDeveloper = hasRole('Developer');

  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      height="100vh"
      textAlign="center"
    >
      <TheLogo w={100} mt={-12} />
      <Box p={4}>
        <Heading color="red.500" mt={8} mb={4}>
          {getErrorTitle(id, error, isDeveloper)}
        </Heading>
        <Text>{getErrorMessage(id, error, isDeveloper)}</Text>
        <Text>
          If you require support email{' '}
          <Link
            color="pink.600"
            href="mailto:datapoints.support@timeshighereducation.com?subject=Service%20Unavailable"
            isExternal
          >
            datapoints.support@timeshighereducation.com
          </Link>
          .
        </Text>
      </Box>
      {FooterComponent && <FooterComponent />}
    </Flex>
  );
};

export default ErrorMessageFull;
